import { type CustomCellEditorProps } from "ag-grid-react";
import { useCallback, useEffect, useMemo } from "react";

import { type DropdownCustomFieldDefinition } from "@btnet/services-btnet/handlers/models/dropdownCustomFieldDefinition";

import { BTSelectItem } from "types/apiResponse/apiResponse";

import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import type { CustomFieldDefinition } from "commonComponents/entity/customField/utils/customFieldDefinition.util";

interface IDropdownCheckListCellEditorParams {
    definition?: CustomFieldDefinition;
}

export const DropdownCheckListCellEditor = (
    props: CustomCellEditorProps<unknown, string> & IDropdownCheckListCellEditorParams,
) => {
    const { value, onValueChange, stopEditing, definition } = props;

    const treeData = useMemo(() => {
        if (!definition) return [];
        const dropdownDef = definition as DropdownCustomFieldDefinition;
        return (dropdownDef.options ?? []).map(
            (opt) => new BTSelectItem({ id: opt.id, name: opt.text, value: opt.id }),
        );
    }, [definition]);

    const selectedValues = value
        ? value
              .split(",")
              .map((s) => parseInt(s.trim(), 10))
              .filter((n) => !isNaN(n))
        : [];

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" || event.key === "Tab") {
                stopEditing();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [stopEditing]);

    const getPopupContainer = useCallback((triggerNode: HTMLElement): HTMLElement => {
        return triggerNode.parentNode instanceof HTMLElement
            ? triggerNode.parentNode
            : document.body;
    }, []);

    const columnWidth = props.column?.getActualWidth();
    const widthStyle = columnWidth
        ? { width: `${Math.max(0, columnWidth - 8)}px`, marginLeft: 4, marginTop: 4 }
        : undefined;

    return (
        <div className="custom-field-select-editor" style={widthStyle}>
            <BTSelect
                id="dropdownCheckListCellEditor"
                data-testid="dropdownCheckListCellEditor"
                value={selectedValues}
                treeData={treeData}
                defaultOpen
                // eslint-disable-next-line jsx-a11y/no-autofocus -- editor opens from a deliberate cell double-click; focus must land in the search input or the editor is unusable
                autoFocus
                dropdownCanExpand={false}
                getPopupContainer={getPopupContainer}
                onChange={(_id, val) => {
                    const selected = (val as number[]) ?? [];
                    onValueChange(selected.length > 0 ? selected.join(",") : null);
                }}
                onBlur={() => {}}
                multiple
            />
        </div>
    );
};
